import { useState, useEffect, useMemo } from "react";
import { useUpdateDeviceParams } from "hooks/useDevices";
import { notify } from "utils/notify";
import { alignLifeguardSetpoints } from "utils/setpointResolution";

interface ShadowParams {
  phSetpoint?: number;
  orpSetpoint?: number;
  tempSetpoint?: number;
  salinitySetpoint?: number;
  setCellOutput?: number;
  vsdSpeed: number;
  vsdIsConnected: boolean;
  phConnected?: boolean;
  orpConnected?: boolean;
  salinityConnected?: boolean;
  tempConnected?: boolean;
  setBoostDuration?: number;
  setBackwashDuration?: number;
}

interface PendingParams {
  phSetpoint: number;
  orpSetpoint: number;
  tempSetpoint: number;
  salinitySetpoint: number;
  setCellOutput: number;
  vsdSpeed: number;
  vsdIsConnected: boolean;
  phConnected: boolean;
  orpConnected: boolean;
  salinityConnected: boolean;
  tempConnected: boolean;
}

function shadowToPending(shadow: ShadowParams): PendingParams {
  return {
    phSetpoint: shadow.phSetpoint || 0,
    orpSetpoint: shadow.orpSetpoint || 0,
    tempSetpoint: shadow.tempSetpoint || 0,
    salinitySetpoint: shadow.salinitySetpoint || 0,
    setCellOutput: shadow.setCellOutput || 0,
    vsdSpeed: shadow.vsdSpeed,
    vsdIsConnected: shadow.vsdIsConnected,
    phConnected: shadow.phConnected || false,
    orpConnected: shadow.orpConnected || false,
    salinityConnected: shadow.salinityConnected || false,
    tempConnected: shadow.tempConnected || false,
  };
}

function countDirtyParams(
  pending: PendingParams,
  baseline: PendingParams | null,
): number {
  if (!baseline) return 0;
  return (Object.keys(pending) as (keyof PendingParams)[]).filter(
    (key) => pending[key] !== baseline[key],
  ).length;
}

interface UseDeviceStatusParamsArgs {
  serialNumber: string;
  shadow: ShadowParams | undefined;
  refetch: () => void;
}

export function useDeviceStatusParams({
  serialNumber,
  shadow,
  refetch,
}: UseDeviceStatusParamsArgs) {
  const updateDeviceParams = useUpdateDeviceParams();
  const [isEditingParams, setIsEditingParams] = useState(false);
  const [updatingParams, setUpdatingParams] = useState(false);
  const [editingParameter, setEditingParameter] = useState<string | null>(null);
  const [baselineParams, setBaselineParams] = useState<PendingParams | null>(
    null,
  );
  const [pendingParams, setPendingParams] = useState<PendingParams>({
    phSetpoint: 0,
    orpSetpoint: 0,
    tempSetpoint: 0,
    salinitySetpoint: 0,
    setCellOutput: 0,
    vsdSpeed: 0,
    vsdIsConnected: false,
    phConnected: false,
    orpConnected: false,
    salinityConnected: false,
    tempConnected: false,
  });

  useEffect(() => {
    if (!shadow || isEditingParams) return;
    const next = shadowToPending(shadow);
    setPendingParams(next);
    setBaselineParams(next);
  }, [shadow, isEditingParams]);

  const dirtyCount = useMemo(
    () => countDirtyParams(pendingParams, baselineParams),
    [pendingParams, baselineParams],
  );
  const hasDirtyParams = dirtyCount > 0;

  const handleEditParameter = (paramName: string) => {
    if (shadow) setBaselineParams(shadowToPending(shadow));
    setEditingParameter(paramName);
    setIsEditingParams(true);
  };

  const handleSaveParams = () => {
    const aligned = alignLifeguardSetpoints(pendingParams);
    setPendingParams(aligned);
    setUpdatingParams(true);

    const targets = {
      ph: aligned.phSetpoint,
      orp: aligned.orpSetpoint,
      temp: aligned.tempSetpoint,
      salinity: aligned.salinitySetpoint,
      cell: aligned.setCellOutput,
      vsdPumpSpeed: aligned.vsdSpeed,
      phConnected: aligned.phConnected,
      orpConnected: aligned.orpConnected,
      salinityConnected: aligned.salinityConnected,
      tempConnected: aligned.tempConnected,
      isVsdPumpConnected: aligned.vsdIsConnected,
      boostDuration: shadow?.setBoostDuration || 24,
      backwashDuration: shadow?.setBackwashDuration || 24,
    };

    updateDeviceParams.mutate(
      { serialNumber, targets },
      {
        onSuccess: () => {
          refetch();
          setIsEditingParams(false);
          setEditingParameter(null);
          setBaselineParams(aligned);
          notify.success("Parameters updated successfully");
        },
        onError: (error) => {
          console.error("Failed to update parameters:", error);
          notify.error("Failed to update parameters");
        },
        onSettled: () => {
          setUpdatingParams(false);
        },
      },
    );
  };

  const handleCancelEdit = () => {
    if (baselineParams) setPendingParams(baselineParams);
    else if (shadow) setPendingParams(shadowToPending(shadow));
    setIsEditingParams(false);
    setEditingParameter(null);
  };

  const toggleParamEditMode = () => {
    if (isEditingParams) handleCancelEdit();
    else {
      if (shadow) setBaselineParams(shadowToPending(shadow));
      setIsEditingParams(true);
      setEditingParameter(null);
    }
  };

  useEffect(() => {
    if (!isEditingParams) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (baselineParams) setPendingParams(baselineParams);
      else if (shadow) setPendingParams(shadowToPending(shadow));
      setIsEditingParams(false);
      setEditingParameter(null);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isEditingParams, baselineParams, shadow]);

  const handleParamChange = (
    param: keyof PendingParams,
    value: PendingParams[keyof PendingParams],
  ) => {
    setPendingParams({ ...pendingParams, [param]: value });
  };

  const handleToggleSensorConnection = (sensorType: string) => {
    if (
      sensorType === "phConnected" ||
      sensorType === "orpConnected" ||
      sensorType === "salinityConnected" ||
      sensorType === "tempConnected"
    ) {
      setPendingParams({
        ...pendingParams,
        [sensorType]: !pendingParams[sensorType],
      });
    }
  };

  return {
    isEditingParams,
    updatingParams,
    editingParameter,
    pendingParams,
    hasDirtyParams,
    dirtyCount,
    handleEditParameter,
    handleSaveParams,
    handleCancelEdit,
    toggleParamEditMode,
    handleParamChange,
    handleToggleSensorConnection,
  };
}
